import { useEffect } from "react";
import {
  ArrowRight,
  Check,
  CloudRain,
  HelpCircle,
  Layers,
  Phone,
  Search,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BeforeAfterSlider } from "@/components/site/BeforeAfter";
import { QuoteForm } from "@/components/site/QuoteForm";
import { business } from "@/config/business";
import { images } from "@/config/images";
import { projectsForService } from "@/config/projects";
import { SERVICE_DETAIL_EXPANSIONS } from "@/config/service-detail-expansions";
import {
  type ServicePage,
  PUBLIC_PRICING_DISCLAIMER,
  publicPricingEnabled,
  relatedServices,
} from "@/config/services";
import { findServiceArea, type ServiceArea } from "@/config/service-areas";
import { useI18n } from "@/i18n";
import { localizeAreaName } from "@/i18n/area-names";
import { trackAnalyticsEvent } from "@/lib/analytics";
import type { PageUi } from "@/i18n/content";
import { localizeServiceArea, localizeServicePage } from "@/i18n/content";
import { getMarketingCopy } from "@/i18n/marketing-copy";
import { localizeProject } from "@/i18n/project-content";
import { absoluteLocalizedUrl } from "@/lib/page-meta";
import type { RouteId } from "@/i18n/route-manifest";

function servicePathwayLinks(service: ServicePage) {
  const links: { href: string; label: string }[] = [
    { href: "/roof-systems", label: "Roof systems" },
  ];
  const isCommercialRelated =
    service.customerTypes.includes("commercial") ||
    service.category === "commercial-strata" ||
    service.category.includes("commercial");

  if (isCommercialRelated) {
    links.push({ href: "/commercial-roofing", label: "Commercial roofing" });
  }
  if (service.customerTypes.includes("strata")) {
    links.push({ href: "/strata-roofing", label: "Strata roofing" });
  }
  if (service.existingService || service.customerTypes.includes("homeowners")) {
    links.push({ href: "/residential-roofing", label: "Residential roofing" });
  }
  if (service.emergencyEligible) {
    links.push({ href: "/emergency-roofing", label: "Emergency roofing" });
  }

  return links;
}

export function ServiceDetailPage({
  service,
  path,
  ui,
}: {
  service: ServicePage;
  path: (href: string) => string;
  ui?: PageUi | undefined;
}) {
  const { locale } = useI18n();
  const marketing = getMarketingCopy(locale);
  const detailExpansion = SERVICE_DETAIL_EXPANSIONS[service.slug];
  const serviceFaqs = detailExpansion?.extraFaqs?.length
    ? [...service.faqs, ...detailExpansion.extraFaqs]
    : service.faqs;
  const image = images[service.image];
  const related = relatedServices(service).map((item) => localizeServicePage(item, locale));
  const relatedAreas = service.relatedAreaSlugs
    .map((slug) => findServiceArea(slug))
    .filter((area): area is ServiceArea => Boolean(area?.confirmed))
    .map((area) => localizeServiceArea(area, locale));
  const showPricing = publicPricingEnabled();
  // Show related galleries when present; verified projects preferred when available.
  const relatedProjects = projectsForService(service.slug)
    .slice(0, 3)
    .map((project) => localizeProject(project, locale));
  const pathwayLinks = servicePathwayLinks(service);

  useEffect(() => {
    trackAnalyticsEvent({
      event: "service_page_view",
      service: service.slug,
      locale,
    });
  }, [service.slug, locale]);

  const serviceRouteId = `service:${service.slug}` as RouteId;
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.metaDescription || service.description,
    serviceType: service.title,
    url: absoluteLocalizedUrl(serviceRouteId, locale),
    provider: {
      "@type": "RoofingContractor",
      name: business.name,
      telephone: business.phone,
      url: business.url,
      areaServed: {
        "@type": "AdministrativeArea",
        name: "Metro Vancouver, British Columbia",
      },
    },
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Metro Vancouver, British Columbia",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: serviceFaqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      {serviceFaqs.length ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      ) : null}

      {/* Hero + definition AEO block + primary CTAs */}
      <section className="relative isolate overflow-hidden bg-slate-deep pt-28 text-primary-foreground md:pt-36">
        <img
          src={image.src}
          alt={service.title}
          width={image.width}
          height={image.height}
          fetchPriority="high"
          className="absolute inset-0 -z-20 size-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(100deg,oklch(0.16_0.06_255/0.98)_0%,oklch(0.16_0.06_255/0.86)_52%,oklch(0.16_0.06_255/0.42)_100%)]" />
        <div className="container-page py-16 md:py-24">
          <nav
            aria-label={ui?.serviceDetail.breadcrumbLabel ?? "Breadcrumb"}
            className="text-xs text-primary-foreground/65"
          >
            <a href={path("/")} className="hover:text-accent">
              {ui?.common.home ?? "Home"}
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <a href={path("/services")} className="hover:text-accent">
              {ui?.common.services ?? "Services"}
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span aria-current="page">{service.shortTitle}</span>
          </nav>
          <p className="eyebrow mt-8">{service.eyebrow}</p>
          <h1 className="display-1 mt-4 max-w-3xl text-balance">{service.title}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/75">
            {service.description}
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button variant="accent" size="xl" asChild>
              <a
                href="#assessment"
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "service_cta_click",
                    cta_location: "service_hero",
                    service: service.slug,
                  })
                }
              >
                {ui?.common.requestAssessment ?? "Request a Roof Assessment"}
              </a>
            </Button>
            <Button variant="outlineDark" size="xl" asChild>
              <a
                href={business.phoneHref}
                className="gap-2"
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "phone_click",
                    link_location: `service_hero_${service.slug}`,
                  })
                }
              >
                <Phone aria-hidden="true" /> {ui?.common.call ?? "Call"} {business.phone}
              </a>
            </Button>
          </div>

          {/* AEO definition block */}
          <div className="mt-12 max-w-3xl rounded-xl border border-primary-foreground/15 bg-primary-foreground/8 p-6 md:p-8">
            <p className="text-xs font-semibold tracking-[0.14em] uppercase text-accent">
              {marketing?.serviceDetail.whatIs(service.shortTitle) ??
                `What is ${service.shortTitle.toLowerCase()}?`}
            </p>
            <p className="mt-3 text-base leading-8 text-primary-foreground/85">
              {service.definition}
            </p>
          </div>
        </div>
      </section>

      {/* What this service solves */}
      <section className="section-y">
        <div className="container-page grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div>
            <p className="eyebrow">{ui?.serviceDetail.whoFor ?? "Who this is for"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {ui?.serviceDetail.conditionFirstTitle ?? "Start with the condition, not the product"}
            </h2>
            <p className="mt-5 text-base leading-8 text-muted-foreground">{service.forWhom}</p>
            <p className="mt-5 text-base leading-8 text-muted-foreground">{service.definition}</p>
          </div>
          <div className="rounded-xl border border-border bg-muted/45 p-7 md:p-9">
            <h2 className="flex items-center gap-3 text-xl font-semibold">
              <Search className="size-5 text-accent" aria-hidden="true" />{" "}
              {ui?.serviceDetail.warningSigns ?? "Common warning signs"}
            </h2>
            <ul className="mt-6 space-y-4">
              {service.warningSigns.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What Revolin evaluates */}
      <section className="section-y border-y border-border bg-primary text-primary-foreground">
        <div className="container-page">
          <p className="eyebrow">
            {ui?.serviceDetail.assessmentDiscipline ?? "Assessment discipline"}
          </p>
          <h2 className="display-2 mt-3 max-w-3xl text-balance">
            {ui?.serviceDetail.evaluatesTitle ?? "What Revolin evaluates"}
          </h2>
          <ol className="mt-12 grid gap-px overflow-hidden rounded-xl border border-primary-foreground/15 bg-primary-foreground/15 sm:grid-cols-2 lg:grid-cols-5">
            {service.evaluates.map((item, index) => (
              <li key={item} className="bg-primary p-6">
                <span className="font-display text-2xl text-accent">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="mt-4 text-sm leading-relaxed text-primary-foreground/72">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Typical causes */}
      <section className="section-y">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div>
            <p className="eyebrow">{marketing?.serviceDetail.causesEyebrow ?? "Typical causes"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {marketing?.serviceDetail.causesTitle ?? "What often drives the need for this work"}
            </h2>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2">
            {service.causes.map((item) => (
              <li
                key={item}
                className="flex gap-3 rounded-xl border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground"
              >
                <Wrench className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Methodology + process */}
      <section className="section-y border-y border-border bg-muted/45">
        <div className="container-page grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <p className="eyebrow">
              {marketing?.serviceDetail.methodologyEyebrow ?? "Methodology"}
            </p>
            <h2 className="display-3 mt-3">
              {marketing?.serviceDetail.methodologyTitle ?? "How Revolin approaches the work"}
            </h2>
            <ol className="mt-8 space-y-4">
              {service.methodology.map((item, index) => (
                <li key={item} className="flex gap-4 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-display text-lg text-accent">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <p className="eyebrow">{marketing?.serviceDetail.processEyebrow ?? "Process"}</p>
            <h2 className="display-3 mt-3">
              {marketing?.serviceDetail.processTitle ?? "From enquiry to completion review"}
            </h2>
            <ol className="mt-8 divide-y divide-border border-y border-border">
              {service.process.map((step, index) => (
                <li key={step} className="grid grid-cols-[2.5rem_1fr] gap-4 py-5">
                  <span className="font-display text-lg text-accent">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="font-medium text-foreground">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {detailExpansion?.paragraphs.length ? (
        <section className="section-y">
          <div className="container-page max-w-3xl">
            <p className="eyebrow">Scope, timeline and pricing model</p>
            <h2 className="display-3 mt-3 text-balance">What to expect on a Metro Vancouver project</h2>
            <div className="mt-8 space-y-5 text-base leading-8 text-muted-foreground">
              {detailExpansion.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 48)}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Materials / systems + solution categories */}
      <section className="section-y">
        <div className="container-page">
          <p className="eyebrow">{ui?.serviceDetail.solutionCategories ?? "Materials & systems"}</p>
          <h2 className="display-2 mt-3 max-w-3xl text-balance">
            {ui?.serviceDetail.solutionsTitle ?? "Materials and detailing categories"}
          </h2>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {service.materials.map((item) => (
              <li
                key={item}
                className="flex gap-3 rounded-xl border border-border bg-card p-5 text-sm leading-relaxed"
              >
                <Layers className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          {service.solutionCategories.length ? (
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {service.solutionCategories.map((item) => (
                <article
                  key={item.title}
                  className="group rounded-xl border border-border bg-card p-7 transition duration-500 hover:-translate-y-1 hover:border-accent/60 hover:shadow-card"
                >
                  <span className="grid size-10 place-items-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/25">
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.body}</p>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* BC climate */}
      <section className="section-y border-y border-border bg-primary text-primary-foreground">
        <div className="container-page grid gap-10 lg:grid-cols-[auto_1fr] lg:items-start lg:gap-16">
          <span className="grid size-14 place-items-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/30">
            <CloudRain className="size-6" aria-hidden="true" />
          </span>
          <div>
            <p className="eyebrow">
              {marketing?.serviceDetail.climateEyebrow ?? "Metro Vancouver climate"}
            </p>
            <h2 className="display-2 mt-3 max-w-3xl text-balance">
              {marketing?.serviceDetail.climateTitle ??
                "BC weather shapes the failure mode and the detail"}
            </h2>
            <p className="mt-5 max-w-3xl text-base leading-8 text-primary-foreground/75">
              {service.bcClimate}
            </p>
          </div>
        </div>
      </section>

      {/* When appropriate / when other service */}
      <section className="section-y">
        <div className="container-page grid gap-8 lg:grid-cols-2">
          <article className="rounded-xl border border-border bg-card p-8">
            <p className="eyebrow">
              {marketing?.serviceDetail.appropriate ?? "When this service is appropriate"}
            </p>
            <p className="mt-4 text-base leading-8 text-muted-foreground">
              {service.whenAppropriate}
            </p>
          </article>
          <article className="rounded-xl border border-border bg-card p-8">
            <p className="eyebrow">
              {marketing?.serviceDetail.alternative ?? "When another service may fit better"}
            </p>
            <p className="mt-4 text-base leading-8 text-muted-foreground">
              {service.whenOtherService}
            </p>
          </article>
        </div>
      </section>

      {/* Repair vs replace */}
      <section className="section-y bg-muted/55">
        <div className="container-page grid gap-8 lg:grid-cols-2">
          <article className="rounded-xl border border-border bg-card p-8">
            <p className="eyebrow">
              {ui?.serviceDetail.repairMakesSense ?? "When repair may make sense"}
            </p>
            <p className="mt-4 text-lg leading-8">{service.repairVsReplace.repair}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-8">
            <p className="eyebrow">
              {ui?.serviceDetail.replacementMakesSense ?? "When replacement may make sense"}
            </p>
            <p className="mt-4 text-lg leading-8">{service.repairVsReplace.replace}</p>
          </article>
        </div>
      </section>

      {/* Scope factors + optional public pricing */}
      <section className="section-y">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div>
            <p className="eyebrow">{ui?.serviceDetail.scope ?? "Scope"}</p>
            <h2 className="display-2 mt-3">
              {ui?.serviceDetail.scopeTitle ?? "What influences the work"}
            </h2>
            <p className="mt-5 leading-7 text-muted-foreground">
              {ui?.serviceDetail.scopeBody ??
                "The actual scope is established after assessment. Revolin Roofing does not publish a one-size-fits-all price, lifespan or assembly because those depend on the building and selected system."}
            </p>
            {showPricing ? (
              <div className="mt-8 rounded-xl border border-accent/40 bg-accent/5 p-6">
                <p className="text-xs font-semibold tracking-[0.14em] uppercase text-accent">
                  {marketing?.serviceDetail.pricingEyebrow ?? "Approximate pricing context"}
                </p>
                <p className="mt-3 text-base font-medium leading-7 text-foreground">
                  {service.publicPriceHint}
                </p>
                <p className="mt-4 text-xs leading-6 text-muted-foreground">
                  {marketing?.serviceDetail.pricingDisclaimer ?? PUBLIC_PRICING_DISCLAIMER}
                </p>
              </div>
            ) : null}
          </div>
          <ul className="grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2">
            {service.scopeFactors.map((factor) => (
              <li key={factor} className="flex items-center gap-3 bg-card p-5 text-sm font-medium">
                <Check className="size-4 shrink-0 text-accent" aria-hidden="true" /> {factor}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related services */}
      {related.length ? (
        <section className="section-y border-y border-border bg-muted/45">
          <div className="container-page">
            <p className="eyebrow">{ui?.common.services ?? "Related services"}</p>
            <h2 className="display-2 mt-3">
              {marketing?.serviceDetail.relatedServicesTitle ??
                "Services often considered together"}
            </h2>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <a
                  key={item.slug}
                  href={path(item.path)}
                  className="group flex flex-col justify-between rounded-xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-card"
                >
                  <div>
                    <p className="text-xs tracking-[0.12em] uppercase text-muted-foreground">
                      {item.eyebrow}
                    </p>
                    <h3 className="mt-2 text-lg font-semibold">{item.shortTitle}</h3>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground line-clamp-3">
                      {item.description}
                    </p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
                    {ui?.common.learnMore ?? "Learn more"}
                    <ArrowRight
                      className="size-4 text-accent transition group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Related service areas */}
      {relatedAreas.length ? (
        <section className="border-b border-border bg-background py-14">
          <div className="container-page">
            <p className="eyebrow">{ui?.common.serviceAreas ?? "Related service areas"}</p>
            <h2 className="display-3 mt-3">
              {ui?.serviceArea.localContext ?? "Local planning context"}
            </h2>
            <div className="mt-7 grid gap-4 sm:grid-cols-3">
              {relatedAreas.map((area) => (
                <a
                  key={area.slug}
                  href={path(`/service-areas/${area.slug}`)}
                  className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 font-semibold transition hover:-translate-y-0.5 hover:border-accent hover:shadow-card"
                >
                  {area.name}
                  <ArrowRight
                    className="size-4 text-accent transition group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Expansion pathway hubs */}
      <section className="border-b border-border bg-muted/30 py-8">
        <div className="container-page">
          <p className="eyebrow">Also useful</p>
          <nav aria-label="Also useful" className="mt-4 flex flex-wrap gap-2">
            {pathwayLinks.map((item) => (
              <a
                key={item.href}
                href={path(item.href)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition hover:border-accent hover:shadow-card"
              >
                {item.label}
                <ArrowRight className="size-3.5 text-accent" aria-hidden="true" />
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Service FAQ */}
      <section className="section-y border-y border-border bg-muted/45">
        <div className="container-page grid gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:gap-20">
          <div>
            <p className="eyebrow">{ui?.common.questions ?? "Questions"}</p>
            <h2 className="display-2 mt-3">
              {ui?.serviceDetail.beforeDecide ?? "Before you decide"}
            </h2>
          </div>
          <div className="space-y-4">
            {serviceFaqs.map((item) => (
              <details
                key={item.question}
                className="group rounded-xl border border-border bg-card p-6"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                  {item.question}
                  <HelpCircle className="size-5 shrink-0 text-accent" aria-hidden="true" />
                </summary>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related verified before/after projects */}
      {relatedProjects.length ? (
        <section className="section-y">
          <div className="container-page">
            <p className="eyebrow">
              {marketing?.serviceDetail.relatedProjectsEyebrow ?? "Related project evidence"}
            </p>
            <h2 className="display-2 mt-3">
              {marketing?.serviceDetail.relatedProjectsTitle ?? "Before and after for this service"}
            </h2>
            <div className="mt-10 grid gap-8 lg:grid-cols-3">
              {relatedProjects.map((project) => (
                <figure key={project.id} className="min-w-0">
                  <BeforeAfterSlider project={project} />
                  <figcaption className="mt-4">
                    <h3 className="text-[0.95rem] font-semibold tracking-tight">
                      <a
                        href={path(`/before-after/${project.slug}`)}
                        className="underline-offset-4 hover:underline"
                      >
                        {project.title}
                      </a>
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {[
                        project.city
                          ? localizeAreaName(project.city, project.city, locale)
                          : project.location,
                        project.system,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Quote form + final CTA */}
      <section id="assessment" className="section-y bg-slate-deep">
        <div className="container-page grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <div className="text-primary-foreground">
            <p className="eyebrow">{ui?.common.nextStep ?? "Next step"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {ui?.serviceDetail.assessmentTitle ?? "Request a clear assessment of your roof"}
            </h2>
            <p className="mt-5 max-w-md leading-7 text-primary-foreground/70">
              {ui?.serviceDetail.assessmentBody ??
                "Tell us what you are seeing and where the property is. We will use that context to plan the right next conversation."}
            </p>
            <Button variant="outlineDark" size="xl" asChild className="mt-8">
              <a
                href={business.phoneHref}
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "phone_click",
                    link_location: `service_footer_${service.slug}`,
                  })
                }
              >
                <Phone aria-hidden="true" /> {ui?.common.call ?? "Call"} {business.phone}
              </a>
            </Button>
            <p className="mt-6 max-w-md text-sm leading-6 text-primary-foreground/70">
              <a
                href={path("/roof-estimate")}
                className="font-medium underline-offset-4 hover:text-accent hover:underline"
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "estimate_cta_click",
                    cta_location: `service_quote_${service.slug}`,
                    lead_type: "planned",
                  })
                }
              >
                Use the planned project form
              </a>
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>
    </>
  );
}
